/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useEffect, useCallback } from 'react'
// Third-party
import { CloseOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import { Modal, Input, App } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'

interface AddTeamModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (teamName: string) => Promise<void>
  divisionName: string
  loading?: boolean
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '24px 16px',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    padding: '0',
    backgroundColor: colors.SECONDARY_300,
  },
}

export const AddTeamModal: React.FC<AddTeamModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  divisionName,
  loading = false,
}) => {
  const [teamName, setTeamName] = useState('')
  const { message } = App.useApp()

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTeamName('')
    }
  }, [isOpen])

  const handleSubmit = useCallback(async () => {
    if (!teamName.trim()) {
      message.error('Please enter a team name')
      return
    }

    try {
      await onSubmit(teamName.trim())
      message.success(`Team "${teamName}" added successfully`)
      setTeamName('')
      onClose()
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to add team'
      message.error(errorMessage)
    }
  }, [teamName, onSubmit, onClose, message])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !loading) {
        handleSubmit()
      }
    },
    [handleSubmit, loading]
  )

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const footer = (
    <div
      css={css`
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        padding: 16px;
      `}
    >
      <Button
        text='Cancel'
        onClick={onClose}
        variant='secondary'
        disabled={loading}
      />
      <Button
        text='Add Team'
        onClick={handleSubmit}
        variant='primary'
        isLoading={loading}
        disabled={loading || !teamName.trim()}
      />
    </div>
  )

  return (
    <Modal
      width={480}
      closeIcon={closeIcon}
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Add Team to {divisionName}
        </Text>
      }
      footer={footer}
      open={isOpen}
      onCancel={onClose}
      centered
      styles={modalStyles}
      maskClosable={false}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
          Team Name
        </Text>
        <Input
          placeholder='Enter team name'
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          css={css`
            background-color: ${colors.SECONDARY_400};
            border: 1px solid ${colors.SECONDARY_200};
            color: ${colors.WHITE};
            padding: 10px 12px;
            border-radius: 8px;

            &::placeholder {
              color: ${colors.NEUTRAL_70};
            }

            &:hover,
            &:focus {
              border-color: ${colors.PRIMARY_300};
              background-color: ${colors.SECONDARY_400};
            }

            &.ant-input:focus,
            &.ant-input-focused {
              box-shadow: 0 0 0 2px rgba(242, 180, 0, 0.2);
            }
          `}
          autoFocus
        />
      </div>
    </Modal>
  )
}

export default AddTeamModal
